import { Layout } from '@/components/Layout';
import { IMAGES } from '@/assets/images';
import { SEO } from '@/components/SEO';
import { motion } from 'framer-motion';
import { Target, Shield, Award, Clock, Users, MapPin, CheckCircle2, Heart, Scale, Lightbulb, BookOpen, Eye, FileText, Brain } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', stiffness: 400, damping: 30 },
};

export default function About() {
  const differentiateurs = [
    {
      icone: Target,
      titre: 'Intérêt de l\'enfant au centre',
      description: 'Toutes nos interventions sont guidées par le bien-être et l\'intérêt supérieur de l\'enfant, dans le respect de ses besoins développementaux et affectifs.',
    },
    {
      icone: Shield,
      titre: 'Neutralité et objectivité',
      description: 'Approche impartiale et professionnelle garantissant une évaluation équitable et rigoureuse de chaque situation familiale.',
    },
    {
      icone: Award,
      titre: 'Expertise spécialisée',
      description: 'Compétences pointues en situations complexes : haut conflit, aliénation parentale, rupture de lien, coparentalité difficile.',
    },
    {
      icone: Scale,
      titre: 'Rigueur clinique',
      description: 'Méthodologie structurée conforme aux standards professionnels et aux lignes directrices reconnues dans le domaine.',
    },
    {
      icone: Heart,
      titre: 'Approche humaine',
      description: 'Respect des familles, empathie et accompagnement bienveillant dans des moments difficiles de leur vie.',
    },
    {
      icone: Clock,
      titre: 'Disponibilité et réactivité',
      description: 'Pratique privée permettant des délais plus courts et une flexibilité adaptée aux besoins urgents des familles et des avocats.',
    },
  ];

  const valeurs = [
    {
      icone: Lightbulb,
      titre: 'Méthodologie rigoureuse',
      description: 'Nos évaluations et interventions suivent des protocoles éprouvés et des standards professionnels reconnus, garantissant la qualité et la crédibilité de nos services.',
    },
    {
      icone: Users,
      titre: 'Collaboration professionnelle',
      description: 'Nous travaillons en étroite collaboration avec les avocats, les intervenants du réseau et les autres professionnels impliqués pour assurer une prise en charge cohérente.',
    },
    {
      icone: CheckCircle2,
      titre: 'Respect des standards',
      description: 'Nos pratiques sont alignées sur les lignes directrices de l\'Association of Family and Conciliation Courts et les normes professionnelles québécoises.',
    },
  ];

  return (
    <Layout>
      <SEO
        title="À propos — Notre approche clinique et professionnelle"
        description="Découvrez le Centre d'expertise familiale de l'Outaouais : notre mission, nos valeurs, notre approche clinique basée sur les meilleures pratiques en psychologie et travail social à Gatineau."
        keywords="travailleur social Gatineau, thérapeute conjugal familial Outaouais, OTSTCFQ, médiation familiale AMFQ, expertise psychosociale professionnelle"
        canonical="/a-propos"
      />
      <div className="min-h-screen">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.CONSULTATION_6}
              alt="Consultation professionnelle"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial="initial"
              animate="animate"
              variants={fadeInUp}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
                À propos du Centre
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Une expertise reconnue au service des familles et des professionnels du droit en Outaouais
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-100px' }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="mb-16">
                <h2 className="text-4xl font-bold mb-6 text-center">Mission et vision</h2>
                <div className="prose prose-lg max-w-none text-foreground/90">
                  <p className="text-lg leading-relaxed mb-6">
                    Le Centre d'expertise familiale de l'Outaouais est un cabinet privé spécialisé en services psychosociaux et familiaux, dédié à l'accompagnement des familles traversant des périodes de séparation, de coparentalité difficile ou de haut conflit.
                  </p>
                  <p className="text-lg leading-relaxed mb-6">
                    Notre mission est de placer <strong>l'intérêt de l'enfant au cœur de chaque intervention</strong>, en offrant des services professionnels rigoureux, neutres et bienveillants. Nous croyons fermement que même dans les situations les plus complexes, il est possible de trouver des solutions durables qui respectent les besoins de tous les membres de la famille.
                  </p>
                  <p className="text-lg leading-relaxed">
                    Notre vision est de devenir la référence régionale en matière d'expertise psychosociale, de médiation familiale, de coaching coparental et de rétablissement du lien parent-enfant, en combinant excellence professionnelle et approche humaine.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={staggerItem} className="mb-16">
                <h2 className="text-4xl font-bold mb-8 text-center">Approche professionnelle</h2>
                <div className="grid md:grid-cols-3 gap-8">
                  {valeurs.map((valeur, index) => {
                    const IconComponent = valeur.icone;
                    return (
                      <motion.div
                        key={index}
                        className="bg-background p-8 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all duration-200"
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                          <IconComponent className="w-7 h-7 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold mb-3">{valeur.titre}</h3>
                        <p className="text-muted-foreground leading-relaxed">{valeur.description}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-6xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-100px' }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-6">Approche clinique et méthodologie</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Une démarche rigoureuse et structurée qui rassure autant les parents que les avocats
                </p>
              </motion.div>

              <motion.div className="grid md:grid-cols-2 gap-12 mb-16" variants={staggerContainer}>
                <motion.div variants={staggerItem} className="bg-card p-8 rounded-2xl border border-border shadow-sm">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                    <Brain className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">Cadres théoriques</h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    Nos interventions s'appuient sur des cadres théoriques reconnus et éprouvés :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Théorie de l'attachement</strong> : Comprendre les liens affectifs parent-enfant et leur impact sur le développement</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Conflit de loyauté</strong> : Identifier et atténuer les pressions exercées sur l'enfant dans les situations de haut conflit</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Coparentalité</strong> : Analyser les dynamiques de collaboration et de communication entre les parents</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Évaluation de l'aliénation parentale</strong> : Approche nuancée et rigoureuse des situations de rejet ou de refus de contact</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div variants={staggerItem} className="bg-card p-8 rounded-2xl border border-border shadow-sm">
                  <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center mb-6">
                    <FileText className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">Méthodes d'évaluation</h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    Notre méthodologie combine plusieurs sources d'information pour une évaluation complète :
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Entrevues individuelles</strong> : Rencontres approfondies avec chaque parent et l'enfant (selon l'âge)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Observations parent-enfant</strong> : Évaluation des interactions et de la qualité du lien</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Sources collatérales</strong> : Consultation de documents (rapports scolaires, médicaux) et contacts avec intervenants</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span className="text-foreground"><strong>Analyse documentaire</strong> : Examen rigoureux des pièces judiciaires et des éléments de preuve</span>
                    </li>
                  </ul>
                </motion.div>
              </motion.div>

              <motion.div variants={staggerItem} className="bg-gradient-to-br from-primary/5 to-secondary/5 p-10 rounded-2xl border border-primary/20">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-4">Positionnement professionnel</h3>
                    <p className="text-lg text-foreground/90 mb-6 leading-relaxed">
                      Notre approche repose sur trois piliers fondamentaux qui garantissent la qualité et la crédibilité de nos interventions :
                    </p>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="bg-background/50 p-6 rounded-xl">
                        <h4 className="font-semibold text-lg mb-2 text-primary">Neutralité</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Impartialité absolue envers toutes les parties, sans préjugé ni parti pris. Chaque parent est écouté et évalué de manière équitable.
                        </p>
                      </div>
                      <div className="bg-background/50 p-6 rounded-xl">
                        <h4 className="font-semibold text-lg mb-2 text-primary">Rigueur</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Méthodologie structurée et systématique, conforme aux standards professionnels et aux lignes directrices reconnues.
                        </p>
                      </div>
                      <div className="bg-background/50 p-6 rounded-xl">
                        <h4 className="font-semibold text-lg mb-2 text-primary">Analyse différenciée</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Distinction claire entre les faits observables et les perceptions subjectives, pour des recommandations fondées sur des données objectives.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-6xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-100px' }}
              variants={staggerContainer}
            >
              <motion.div variants={staggerItem} className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-6">Pourquoi nous choisir</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Six raisons qui font du Centre d'expertise familiale de l'Outaouais votre partenaire de confiance
                </p>
              </motion.div>

              <motion.div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                variants={staggerContainer}
              >
                {differentiateurs.map((diff, index) => {
                  const IconComponent = diff.icone;
                  return (
                    <motion.div
                      key={index}
                      variants={staggerItem}
                      className="bg-card p-8 rounded-2xl border border-border shadow-sm hover:shadow-lg transition-all duration-300"
                      whileHover={{ y: -4 }}
                    >
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 shadow-lg">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-semibold mb-4">{diff.titre}</h3>
                      <p className="text-muted-foreground leading-relaxed">{diff.description}</p>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp}
            >
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                    <MapPin className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-4xl font-bold mb-6">Zone de service</h2>
                  <div className="prose prose-lg text-foreground/90">
                    <p className="text-lg leading-relaxed mb-4">
                      Nous desservons principalement la <strong>région de l'Outaouais</strong>, incluant Gatineau et les municipalités environnantes.
                    </p>
                    <p className="text-lg leading-relaxed mb-4">
                      Selon la nature du mandat et les circonstances, nous pouvons également offrir nos services dans d'autres régions du Québec, particulièrement pour les expertises psychosociales mandatées par le tribunal.
                    </p>
                    <p className="text-lg leading-relaxed">
                      Notre pratique privée nous permet d'offrir une flexibilité géographique adaptée aux besoins des familles et des professionnels du droit.
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <img
                    src={IMAGES.SERVICES_FAMILY_1}
                    alt="Services familiaux professionnels"
                    className="rounded-2xl shadow-2xl w-full h-auto"
                  />
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-primary/20 to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold mb-6">Certifications et affiliations</h2>
              <div className="bg-card p-12 rounded-2xl border border-border shadow-sm">
                <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                  Nos pratiques sont alignées sur les lignes directrices de l'<strong>Association of Family and Conciliation Courts</strong> et respectent les normes professionnelles établies par les ordres professionnels du Québec.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Nous nous engageons à maintenir les plus hauts standards de qualité et d'éthique professionnelle dans tous nos services, en participant régulièrement à des formations continues et en restant à jour avec les meilleures pratiques du domaine.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: '-100px' }}
              variants={fadeInUp}
            >
              <h2 className="text-4xl font-bold mb-6">Prêt à nous rencontrer?</h2>
              <p className="text-xl text-muted-foreground mb-10 leading-relaxed">
                Que vous soyez un parent cherchant du soutien ou un avocat recherchant une expertise crédible, nous sommes là pour vous accompagner avec professionnalisme et bienveillance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="#contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-primary rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Prendre rendez-vous
                </motion.a>
                <motion.a
                  href="#contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary bg-white border-2 border-primary rounded-xl hover:bg-primary/5 transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Nous contacter
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
